import {
  getCurrentOwnerOrg,
  getCurrentOwnerSub,
  isHeldOp,
  peekOutbox,
  wipeDatabaseForClear,
} from "./db";
import { findOpLessLocalSheets } from "./rebuild";
import { countUnsavedEdits } from "./unsavedEdits";
import { causeForOp } from "./failureActions";
import { getUnsyncedManualRideCount, subscribeManualRides } from "../store/manualRides";
import { getPresenceInstanceId, resetPresenceInstanceId } from "../util/presenceInstanceId";
import { fullSignOut } from "../auth/signOut";

// #3518 Reset tablet, allowed by the show PIN on the scribe's own station
// (#3568): reset of a scribe tablet that never loses un-uploaded scores.
// Orchestration only, injectable like local/rebuild.js so a test drives the
// exact call sites against a fake engine and fake api.
//
// Order of a clear:
//   1. Stop the engine's timers so nothing touches the outbox or database.
//   2. Online: send another login's ops under this session, flush the outbox.
//   3. Count the work still only on this tablet: outbox ops, held ops, unsaved
//      edits, op-less local sheets, local manual rides.
//   4. No work: offline refuses, online records the clear, then wipes.
//      Work, not forced: blocked (the caller reports what and how many).
//      Work, forced, online: the full station snapshot is uploaded and the
//      server must confirm it; then the clear is recorded; then it wipes.
//      Work, forced, offline: the caller chooses wait (re-run once online) or
//      download a file; the download saves a receipt that survives the wipe.
//   5. A snapshot or record that fails wipes NOTHING.
//
// Wipe order: the database, then storage (keeping only the deferred receipt),
// then a brand new station GUID, then sign out last (it navigates away).

// Survives the wipe on purpose: the offline download path cannot tell the server
// before the wipe, so the new station posts this receipt once it is online.
export const DEFERRED_RECEIPT_KEY = "scribe-fe.clearMachine.deferredReceipt";

export async function countUnuploadedWork() {
  const sub = getCurrentOwnerSub();
  const ops = await peekOutbox();
  const held = ops.filter((op) => isHeldOp(op, sub)).length;
  const counts = {
    outbox: ops.length - held,
    held,
    unsavedEdits: countUnsavedEdits(),
    localDrafts: (await findOpLessLocalSheets()).length,
    manualRides: getUnsyncedManualRideCount(),
  };
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  return { counts, total };
}

export function readDeferredReceipt() {
  try {
    const raw = localStorage.getItem(DEFERRED_RECEIPT_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearDeferredReceipt() {
  try {
    localStorage.removeItem(DEFERRED_RECEIPT_KEY);
  } catch {
    // Non-fatal: the next post of the same receipt is an audit duplicate only.
  }
}

// The wipe itself. `removeUser` is react-oidc-context's; `redirect` is the
// captured sign-out navigation seam (undefined = fullSignOut's default).
// `receiptJson` (the offline download path only) is written back AFTER the new
// station GUID exists, into the one key this wipe leaves alone.
export async function wipeThisTablet({ removeUser, redirect, receiptJson = null } = {}) {
  await wipeDatabaseForClear();
  try {
    localStorage.clear();
  } catch {
    // Storage may be blocked; nothing more to clear in that case.
  }
  try {
    sessionStorage.clear();
  } catch {
    // Same.
  }
  resetPresenceInstanceId();
  if (receiptJson) {
    try {
      localStorage.setItem(DEFERRED_RECEIPT_KEY, receiptJson);
    } catch {
      // Probed writable before the wipe began; nothing else to do here.
    }
  }
  await fullSignOut(removeUser, redirect ? { redirect } : undefined);
}

const recordBody = ({ via, forced, counts, snapshot }) => ({ via, forced, counts, snapshot });

// deps: { syncEngine, api: { record(stationId, body), reportBlocked(stationId, body) },
//   sendSnapshot(): Promise<{ok, snapshotKey}>, downloadSnapshot(): Promise<void>,
//   removeUser, redirect, adminSub }
// opts: { via: "tablet" | "station-monitor", force, offlineChoice: null | "download" }
export async function clearTheMachine(deps, { via, force = false, offlineChoice = null }) {
  const { syncEngine, api, sendSnapshot, downloadSnapshot, removeUser, redirect, adminSub } = deps;
  const stationId = getPresenceInstanceId();
  const online = syncEngine.isOnline();
  let wiping = false;
  syncEngine.stop();
  try {
    if (online) {
      await syncEngine.claimForeignOps?.();
      await syncEngine.flush();
    }
    const { counts, total } = await countUnuploadedWork();
    const stillOnline = syncEngine.isOnline();

    if (total === 0) {
      if (!stillOnline) return { ok: false, reason: "offline", counts };
      const rec = await api.record(stationId, recordBody({ via, forced: false, counts, snapshot: null })).then(
        () => true,
        () => false,
      );
      if (!rec) return { ok: false, reason: "recordFailed", counts };
      wiping = true;
      await wipeThisTablet({ removeUser, redirect });
      return { ok: true, counts };
    }

    if (!force) {
      if (via === "station-monitor") {
        await api.reportBlocked(stationId, { counts }).catch(() => {});
      }
      return { ok: false, reason: "blocked", counts };
    }

    // Offline, or online with no show to upload to (the snapshot route is per
    // show): the only way to keep the scores off this tablet is a downloaded file.
    if (!stillOnline || offlineChoice === "download") {
      if (offlineChoice !== "download") return { ok: false, reason: "forceOffline", counts };
      const receiptJson = JSON.stringify({
        stationId,
        counts,
        claimedBy: adminSub ?? null,
        claimedAt: new Date().toISOString(),
      });
      try {
        await downloadSnapshot();
        // Probe that the receipt can be stored before anything is wiped.
        localStorage.setItem(DEFERRED_RECEIPT_KEY, receiptJson);
      } catch {
        return { ok: false, reason: "downloadFailed", counts };
      }
      wiping = true;
      await wipeThisTablet({ removeUser, redirect, receiptJson });
      return { ok: true, deferred: true, counts };
    }

    let sent = { ok: false };
    try {
      sent = await sendSnapshot();
    } catch {
      // An unknown outcome is a failed upload: never wipe on it.
    }
    if (sent?.reason === "noUploader") return { ok: false, reason: "noShow", counts };
    // Only a confirmed snapshot WITH its stored key counts as uploaded.
    if (!sent?.ok || !sent.snapshotKey) return { ok: false, reason: "snapshotFailed", counts };
    const snapshot = { kind: "uploaded", snapshotKey: sent.snapshotKey };
    const rec = await api.record(stationId, recordBody({ via, forced: true, counts, snapshot })).then(
      () => true,
      () => false,
    );
    if (!rec) return { ok: false, reason: "recordFailed", counts };
    wiping = true;
    await wipeThisTablet({ removeUser, redirect });
    return { ok: true, counts };
  } finally {
    if (!wiping) syncEngine.start();
  }
}

// ---------------------------------------------------------------------------
// #3568 Admin Options > Reset tablet. The show PIN on the tablet is the only
// authority needed (the server lets the signed-in scribe clear its OWN station).
// The rule that outranks everything here: a sheet that was neither SENT nor SAVED
// TO A FILE is never erased. There is no "erase anyway".
//
//   Online:  send every Unsent sheet first (flush). If one does not send, stop,
//            say why (the server's reason) and erase nothing. Anything the outbox
//            does not hold (op-less drafts, hand-added rides) goes up as a full
//            station snapshot the office must confirm. Then record, then wipe.
//   Offline: Reset first needs the Unsent sheets saved to a file (a USB stick).
//            The caller saves it (buildUnsentSheetsFile) and passes the receipt
//            back as `fileSaved`; this re-checks that the file still covers
//            everything on the tablet before it wipes.
// ---------------------------------------------------------------------------

function readUnsyncedManualRides() {
  let list = [];
  const unsubscribe = subscribeManualRides((l) => {
    list = l;
  });
  unsubscribe();
  return list.filter((r) => !r.serverId);
}

// Small stable string hash (djb2) of a value's JSON. Not security, only change detection.
function contentHash(value) {
  const text = JSON.stringify(value) ?? "";
  let h = 5381;
  for (let i = 0; i < text.length; i += 1) h = ((h * 33) ^ text.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

// Everything on this tablet that exists nowhere else, as plain data. Items are
// stable strings so a saved file can be compared against the tablet later.
export async function collectUnsent() {
  const ops = await peekOutbox();
  const localDrafts = await findOpLessLocalSheets();
  const manualRides = readUnsyncedManualRides();
  const items = [
    ...ops.map((op) => `op:${op.opId}@${op.rev ?? 0}`),
    ...localDrafts.map((row) => `draft:${row.id ?? `${row.rideId}::${row.judgeId}`}@${row.updatedAt ?? ""}`),
    // A hand-added ride has no version or updatedAt, so fingerprint its content: a ride
    // edited after the file was saved must make the file stale.
    ...manualRides.map((r) => `ride:${r.tempId ?? r.id}@${contentHash(r)}`),
  ];
  return { ops, localDrafts, manualRides, items };
}

function unsentSheetTotal({ ops, localDrafts, manualRides }) {
  return new Set(ops.map((op) => op.entityKey ?? op.opId)).size + localDrafts.length + manualRides.length;
}

// How many Unsent sheets the tablet holds: distinct outbox entities (a sheet with
// three queued ops is one sheet), plus op-less drafts and hand-added rides.
export async function countUnsentSheets() {
  return unsentSheetTotal(await collectUnsent());
}

export const UNSENT_FILE_KIND = "scribe-unsent-sheets";

// The saved file (card 3571 imports it). One JSON object:
//   kind        "scribe-unsent-sheets"        version  1
//   savedAt     ISO time the file was saved
//   tabletId    the station GUID (presenceInstanceId) this tablet had
//   station     { id, name }                   name may be null
//   show        { id, name }                   the show open on the tablet, or nulls
//   org         { id }                         the org the tablet was working in
//   count       number of Unsent sheets in the file
//   sheets      every outbox op exactly as the outbox would send it (opId, seq,
//               type, entityKey, payload, status, attempts, createdAt, owner, ...)
//   localDrafts score sheet rows that were changed but never queued
//   manualRides rides added by hand that never reached the server
//   items       opaque strings, not part of the import contract (Reset uses them
//               to prove the file still covers the tablet)
export function buildUnsentSheetsFile({ collected, tabletId, stationName = null, show = null, orgId = null, now = new Date() }) {
  return {
    kind: UNSENT_FILE_KIND,
    version: 1,
    savedAt: now.toISOString(),
    tabletId,
    station: { id: tabletId, name: stationName },
    show: { id: show?.id ?? null, name: show?.name ?? null },
    org: { id: orgId ?? getCurrentOwnerOrg() ?? null },
    count: unsentSheetTotal(collected),
    sheets: collected.ops,
    localDrafts: collected.localDrafts,
    manualRides: collected.manualRides,
    items: collected.items,
  };
}

export function unsentSheetsFilename({ tabletId, stationName }) {
  const tail = String(tabletId ?? "").slice(-8) || "tablet";
  const slug = String(stationName ?? "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${tail}-${slug || "station"}-sheets.json`;
}

// deps: { syncEngine, api: { record(stationId, body) }, sendSnapshot(), sendHeartbeat?(), removeUser, redirect }
// opts: { fileSaved: null | { items: string[] } }  (the receipt of a saved file)
// Result: { ok:true } (the page signs out) or { ok:false, reason, ... } with NOTHING erased:
//   sendFailed (why, remaining), needsFile, fileStale, offline, snapshotFailed, recordFailed
const FLUSH_ATTEMPTS = 4;
const FLUSH_RETRY_MS = 750;

// The server's reason for a refusal (its scoreFailure message), or null.
function serverReasonOf(err) {
  const body = err?.response?.data;
  const why = body?.data?.message ?? body?.message ?? null;
  return typeof why === "string" && why ? why : null;
}

export async function resetTablet(deps, { fileSaved = null } = {}) {
  const { syncEngine, api, sendSnapshot, sendHeartbeat, showId = null, removeUser, redirect } = deps;
  const stationId = getPresenceInstanceId();
  let wiping = false;
  syncEngine.stop();
  try {
    if (syncEngine.isOnline()) {
      await syncEngine.claimForeignOps?.();
      const sub = getCurrentOwnerSub();
      // flush() returns at once when a drain pass is already running (the 5s loop, or
      // the pass stop() just aborted is still unwinding), which would read as "stuck"
      // while the sheets are in fact on their way. Ask again a few times before saying so.
      let stuck = [];
      for (let attempt = 0; attempt < FLUSH_ATTEMPTS; attempt += 1) {
        await syncEngine.flush();
        stuck = (await peekOutbox()).filter((op) => !isHeldOp(op, sub));
        if (stuck.length === 0) break;
        if (attempt < FLUSH_ATTEMPTS - 1) await new Promise((resolve) => setTimeout(resolve, FLUSH_RETRY_MS));
      }
      if (stuck.length > 0) {
        return { ok: false, reason: "sendFailed", why: causeForOp(stuck[0]), remaining: stuck.length };
      }
    }
    const { counts, total } = await countUnuploadedWork();

    // A saved file already covers everything on the tablet (the caller got here via
    // needsFile): both with no connection and with a connection but no show open (no
    // place to upload to), the file route is the same stale-check + deferred receipt.
    const useFile = total > 0 && Boolean(fileSaved);

    // Edits still in memory are neither sent nor in a saved file, so they are lost by a
    // wipe either way. Refuse whenever the file is the only copy (offline, or online on
    // the file route), and say to save.
    if ((!syncEngine.isOnline() || useFile) && counts.unsavedEdits > 0) return { ok: false, reason: "unsavedEdits", counts };

    if (syncEngine.isOnline() && !useFile) {
      const record = (body) =>
        api.record(stationId, recordBody(body)).then(
          () => ({ ok: true }),
          (err) => ({ ok: false, why: serverReasonOf(err) }),
        );
      // The server proves "this is THIS scribe's own station" from the presence row,
      // which expires after about an hour offline. A tablet that just came back online
      // would be refused with no staff path, so refresh the row (the same heartbeat the
      // tablet already sends) right before asking. The beat needs the show the row
      // belongs to: with no show the clear would be refused, so say so up front.
      // A failed beat is not fatal: if the clear is still refused, record() returns the reason.
      const beat = async () => {
        if (!showId) return false;
        try {
          await sendHeartbeat?.();
        } catch {
          // swallowed on purpose
        }
        return true;
      };
      if (total === 0) {
        if (!(await beat())) return { ok: false, reason: "noShow", counts };
        const rec = await record({ via: "tablet", forced: false, counts, snapshot: null });
        if (!rec.ok) return { ok: false, reason: "recordFailed", why: rec.why, counts };
        wiping = true;
        await wipeThisTablet({ removeUser, redirect });
        return { ok: true, counts };
      }
      let sent = { ok: false };
      try {
        sent = await sendSnapshot();
      } catch {
        // An unknown outcome is a failed upload: never wipe on it.
      }
      // No show open means nowhere to upload: the file is the only safe copy.
      if (sent?.reason === "noUploader") return { ok: false, reason: "needsFile", counts };
      if (!sent?.ok || !sent.snapshotKey) return { ok: false, reason: "snapshotFailed", counts };
      const snapshot = { kind: "uploaded", snapshotKey: sent.snapshotKey };
      if (!(await beat())) return { ok: false, reason: "noShow", counts };
      const rec = await record({ via: "tablet", forced: true, counts, snapshot });
      if (!rec.ok) return { ok: false, reason: "recordFailed", why: rec.why, counts };
      wiping = true;
      await wipeThisTablet({ removeUser, redirect });
      return { ok: true, counts };
    }

    // Offline (or online with the saved file in hand). Nothing to save and nothing to
    // tell the office: Reset has no safe way yet.
    if (total === 0) return { ok: false, reason: "offline", counts };
    if (!fileSaved) return { ok: false, reason: "needsFile", counts };
    const saved = new Set(fileSaved.items ?? []);
    const now = (await collectUnsent()).items;
    if (now.some((item) => !saved.has(item))) return { ok: false, reason: "fileStale", counts };
    const receiptJson = JSON.stringify({
      stationId,
      counts,
      claimedBy: getCurrentOwnerSub() ?? null,
      claimedAt: new Date().toISOString(),
    });
    try {
      // Probe that the receipt can be stored before anything is wiped.
      localStorage.setItem(DEFERRED_RECEIPT_KEY, receiptJson);
    } catch {
      return { ok: false, reason: "fileStale", counts };
    }
    wiping = true;
    await wipeThisTablet({ removeUser, redirect, receiptJson });
    return { ok: true, deferred: true, counts };
  } finally {
    if (!wiping) syncEngine.start();
  }
}

// Posts the saved download receipt from the NEW station, once it is online and
// signed in. Recorded against the OLD station GUID, marked deferred.
export async function postDeferredReceipt({ api }) {
  const receipt = readDeferredReceipt();
  if (!receipt?.stationId) return { posted: false };
  try {
    await api.record(receipt.stationId, {
      via: "tablet",
      forced: true,
      deferred: true,
      claimedBy: receipt.claimedBy ?? null,
      claimedAt: receipt.claimedAt ?? null,
      counts: receipt.counts,
      snapshot: { kind: "downloaded", snapshotKey: null },
    });
  } catch {
    return { posted: false };
  }
  clearDeferredReceipt();
  return { posted: true };
}
